import { useDispatch, useSelector } from "react-redux";

import { Col, Row, Tabs, TabsProps, Typography } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";

import usePageLoading from "hooks/usePageLoading";

import InvestigationCardList from "js/components/Investigations/InvestigationCardList";
import InvestigationTable from "js/components/Investigations/InvestigationTable";
import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonText,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import TenantReportCardListForOrganization from "js/components/Tenants/TenantReportCardListForOrganization";
import TenantReportsTableForOrganization from "js/components/Tenants/TenantReportsTableForOrganization";
import { Button } from "js/components/ui/button";

import { loadOrganizationData } from "../../../store/features/organization/organizationActions";

const { Title } = Typography;
const OrganizationReports = () => {
  const dispatch = useDispatch();
  const { organizationData, loading } = useSelector(
    (state: any) => state.entities.organization,
  );

  const hasCreditHistModule =
    organizationData?.modules.includes("credit_history") ?? false;
  const hasTenantModule = organizationData?.modules.includes("tenant") ?? false;

  const screen = useBreakpoint();

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading: loading && !organizationData,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    dispatch(loadOrganizationData());
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <SkeletonHeading className="h-9 w-80" />
      <SkeletonHeading className="h-6 w-48" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-6">
      <div className="flex gap-4 border-b border-slate-200">
        <SkeletonBlock className="h-10 w-52 rounded-t-lg" />
        <SkeletonBlock className="h-10 w-48 rounded-t-lg" />
      </div>
      <TableSkeleton columns={5} rows={5} />
    </div>
  );

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Reportes de Hist. Credit.",
      children: (
        <>
          <Row style={{ marginTop: "24px" }} gutter={[16, 16]}>
            <Col xs={24}>
              {screen.md ? (
                <InvestigationTable
                  showAdvisorColumn={true}
                  isOrganization={true}
                />
              ) : (
                <InvestigationCardList
                  showAdvisorColumn={true}
                  isOrganization={true}
                />
              )}
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "2",
      label: "Reportes de Inquilino",
      children: (
        <>
          <Row style={{ marginTop: "24px" }} gutter={[16, 16]}>
            <Col xs={24}>
              {screen.md ? (
                <TenantReportsTableForOrganization showAdvisorColumn={true} />
              ) : (
                <TenantReportCardListForOrganization showAdvisorColumn={true} />
              )}
            </Col>
          </Row>
        </>
      ),
    },
  ];

  return (
    <PageLoadingContainer
      showSkeleton={showSkeleton}
      slowState={slowStateBanner}
      headerSkeleton={headerSkeleton}
      contentSkeleton={contentSkeleton}
      className="space-y-8"
    >
      <div id="home" className="space-y-6">
        <Row justify="space-around" gutter={[16, 16]}>
          <Col
            xs={24}
            sm={12}
            md={24}
            lg={12}
            style={{ display: "flex", alignItems: "left" }}
          >
            <Title level={2}>Reportes de la Organización</Title>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={24}
            lg={6}
            style={{ display: "flex", alignItems: "right" }}
          >
            {organizationData && (
              <Title level={4}>{organizationData.name}</Title>
            )}
          </Col>
        </Row>

        {hasCreditHistModule && hasTenantModule ? (
          <Tabs style={{ marginTop: 30 }} defaultActiveKey="1" items={items} />
        ) : hasCreditHistModule ? (
          <Row style={{ marginTop: "24px" }} gutter={[16, 16]}>
            <Col xs={24}>
              {screen.md ? (
                <InvestigationTable
                  showAdvisorColumn={true}
                  isOrganization={true}
                />
              ) : (
                <InvestigationCardList
                  showAdvisorColumn={true}
                  isOrganization={false}
                />
              )}
            </Col>
          </Row>
        ) : hasTenantModule ? (
          <Row style={{ marginTop: "24px" }} gutter={[16, 16]}>
            <Col xs={24}>
              {screen.md ? (
                <TenantReportsTableForOrganization showAdvisorColumn={true} />
              ) : (
                <TenantReportCardListForOrganization showAdvisorColumn={true} />
              )}
            </Col>
          </Row>
        ) : (
          <h1>Hola</h1>
        )}
      </div>
    </PageLoadingContainer>
  );
};

export default OrganizationReports;
